import classNames from 'classnames';
import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ReactComponent as Education } from 'src/assets/services/education.svg';
import { ReactComponent as UsefulInfo } from 'src/assets/services/info.svg';
import { ReactComponent as LegalHelp } from 'src/assets/services/legal.svg';
import { ReactComponent as BusinessMap } from 'src/assets/services/map.svg';
import { ReactComponent as MedServices } from 'src/assets/services/med.svg';
import { ReactComponent as PsychologicalHelp } from 'src/assets/services/psych.svg';
import { ReactComponent as Work } from 'src/assets/services/work.svg';
import SearchInput from 'src/components/form/search-input/search-input';
import PageHeader from 'src/components/page-header/page-header';
import FilterTags from 'src/components/tags/filter-tags';
import * as p from 'src/constants/page-classnames';
import * as r from 'src/constants/routes';
import EmptyPlaceholder from './empty-placeholder';
import css from './global-search.module.scss';
import SearchServiceBlock from './search-service-block';

import { useGetCategories, useGetServiceBlock, useGetCourses } from './data/queries';
import CourseBlock from './course-block';
import { ServiceClasses } from 'src/types';

export default function GlobalSearch() {
  const [searchParams, setSearchParams] = useSearchParams();
  const searchValue = searchParams?.get('search') ?? '';
  const tag = searchParams?.get('tag');

  const [searchData, setSearchData] = useState(searchValue);

  const { data: categories = [] } = useGetCategories();

  const getCategoryId = (name: string) => categories.find((c: { name: string; id: string }) => c.name === name)?.id;

  const { data: courses = { totalCount: 0, items: [] }, isPending: pendingCourses } = useGetCourses({
    search: searchData
  });

  const { data: educationInfos = { totalServicesCount: 0, items: [] }, isPending: pendingEducation } =
    useGetServiceBlock({
      search: searchData,
      categoryId: getCategoryId(r.EDUCATION_INFO.title)
    });

  const { data: careerInfos = { totalServicesCount: 0, items: [] }, isPending: pendingCareer } = useGetServiceBlock({
    search: searchData,
    categoryId: getCategoryId(r.CAREER_INFO.title)
  });

  const { data: legalHelps = { totalServicesCount: 0, items: [] }, isPending: pendinglegal } = useGetServiceBlock({
    search: searchData,
    categoryId: getCategoryId(r.LEGAL_HELP.title)
  });

  const { data: medicalServices = { totalServicesCount: 0, items: [] }, isPending: pendingMed } = useGetServiceBlock({
    search: searchData,
    categoryId: getCategoryId(r.MEDICAL_SERVICES.title)
  });

  const { data: psychologicalHelps = { totalServicesCount: 0, items: [] }, isPending: pendingPsyh } =
    useGetServiceBlock({
      search: searchData,
      categoryId: getCategoryId(r.PSYCHOLOGICAL_HELP.title)
    });

  const { data: usefulInfos = { totalServicesCount: 0, items: [] }, isPending: pendingUseful } = useGetServiceBlock({
    search: searchData,
    categoryId: getCategoryId(r.USEFUL_INFO.title)
  });

  const { data: vetLocations = { totalServicesCount: 0, items: [] }, isPending: pendingLocations } = useGetServiceBlock(
    {
      search: searchData,
      categoryId: getCategoryId(r.VETERAN_LOCATIONS.title)
    }
  );

  const defaultTagName = { id: -1, name: 'Усі', count: 0 };

  const tags = useMemo(
    () => [
      {
        id: 0,
        name: r.USEFUL_INFO.title,
        count: usefulInfos.totalServicesCount,
        items: usefulInfos,
        icon: <UsefulInfo />,
        className: p.USEFUL_INFO,
        route: r.USEFUL_INFO
      },
      {
        id: 1,
        name: r.PSYCHOLOGICAL_HELP.title,
        count: psychologicalHelps.totalServicesCount,
        items: psychologicalHelps,
        icon: <PsychologicalHelp />,
        className: p.PSYCHOLOGICAL_HELP,
        route: r.PSYCHOLOGICAL_HELP
      },
      {
        id: 2,
        name: r.LEGAL_HELP.title,
        count: legalHelps.totalServicesCount,
        items: legalHelps,
        icon: <LegalHelp />,
        className: p.LEGAL_HELP,
        route: r.LEGAL_HELP
      },
      {
        id: 3,
        name: r.MEDICAL_SERVICES.title,
        count: medicalServices.totalServicesCount,
        items: medicalServices,
        icon: <MedServices />,
        className: p.MEDICAL_SERVICES,
        route: r.MEDICAL_SERVICES
      },
      {
        id: 4,
        name: r.EDUCATION_INFO.title,
        count: educationInfos.totalServicesCount,
        items: educationInfos,
        icon: <Education />,
        className: p.EDUCATION,
        route: r.EDUCATION_INFO
      },
      {
        id: 5,
        name: r.VETERAN_LOCATIONS.title,
        count: vetLocations.totalServicesCount,
        items: vetLocations,
        icon: <BusinessMap />,
        className: p.VETERAN_LOCATIONS,
        route: r.VETERAN_LOCATIONS
      },
      {
        id: 6,
        name: r.CAREER_INFO.title,
        count: careerInfos.totalServicesCount,
        items: careerInfos,
        icon: <Work />,
        className: p.CAREER_INFO,
        route: r.CAREER_INFO
      },
      {
        id: 7,
        name: r.EDUCATION_COURSES.title,
        count: courses.totalCount,
        items: [],
        icon: <></>,
        className: p.EDUCATION,
        route: r.EDUCATION_COURSES
      }
    ],
    [
      careerInfos,
      courses.totalCount,
      educationInfos,
      legalHelps,
      medicalServices,
      psychologicalHelps,
      usefulInfos,
      vetLocations
    ]
  );

  const [searchTag, setSearchTag] = useState(tag ? tags.find((p) => p.name.includes(tag))?.id : defaultTagName.id);

  const isLoading =
    (pendingCourses ||
      pendingLocations ||
      pendingUseful ||
      pendingPsyh ||
      pendingMed ||
      pendinglegal ||
      pendingCareer ||
      pendingEducation) &&
    searchData.length !== 0;

  const totalResultsCount =
    searchData?.length !== 0
      ? courses.totalCount +
        vetLocations.totalServicesCount +
        usefulInfos.totalServicesCount +
        psychologicalHelps.totalServicesCount +
        medicalServices.totalServicesCount +
        legalHelps.totalServicesCount +
        careerInfos.totalServicesCount +
        educationInfos.totalServicesCount
      : 0;

  const skeletons = [...Array(3).keys()].map((i) => <span key={i} className={css.skeleton} />);

  function handleSearch(value: string) {
    const nextSearchParams = new URLSearchParams(searchParams);
    const trimmedValue = value?.trim();
    trimmedValue?.length > 0 ? nextSearchParams.set('search', trimmedValue) : nextSearchParams.delete('search');
    if (nextSearchParams.toString() !== searchParams.toString()) setSearchParams(nextSearchParams);
    setSearchData(trimmedValue);
  }

  function handleTagChange(tag: number) {
    const nextSearchParams = new URLSearchParams(searchParams);
    tag >= 0 ? nextSearchParams.set('tag', tags.find((p) => p.id === tag)!.name) : nextSearchParams.delete('tag');
    if (nextSearchParams.toString() !== searchParams.toString()) setSearchParams(nextSearchParams);
    setSearchTag(tag);
  }

  const generateServiceItems = () => {
    const items =
      searchTag === -1
        ? tags.filter((t) => t.count !== 0 && t.id !== 7)
        : tags.filter((t) => t.id === searchTag && t.count !== 0 && t.id !== 7);

    return items.map((i) => (
      <SearchServiceBlock
        key={i.id}
        icon={i.icon}
        categoryItems={i.items}
        search={searchData}
        className={i.className as ServiceClasses}
        route={i.route}
      />
    ));
  };

  useEffect(() => {
    !tag ? setSearchTag(defaultTagName.id) : setSearchTag(tags.find((p) => p.name.includes(tag))?.id);
  }, [defaultTagName.id, tag, tags]);

  useEffect(() => {
    (!searchValue.includes(searchData) || searchData.length === 0) && setSearchData(searchValue);
  }, [searchData, searchValue]);

  return (
    <main className={classNames(css.globalSearchPage, totalResultsCount && totalResultsCount === 0 ? css.empty : '')}>
      <PageHeader
        title='Глобальний пошук'
        description='Знайдіть потрібне за кілька кліків'
        filter={
          <div className={css.searchInputGroup}>
            <SearchInput
              id='global-search'
              name='global-search'
              placeholder='Пошук'
              initialValue={searchData}
              onSearch={handleSearch}
              className={css.globalSearch}
            />
            {!isLoading && <p>За Вашим запитом знайдено матеріалів: {totalResultsCount}</p>}
          </div>
        }
      />
      <div className={css.container}>
        <div className={css.filters}>
          {!isLoading && totalResultsCount > 0 && (
            <FilterTags
              title='Фільтруйте за типом послуг та матеріалів'
              tags={[defaultTagName.id].concat(tags.filter((t) => t.count > 0).map((t) => t.id))}
              selectedTag={searchTag}
              onClick={handleTagChange}
              calcName={(t) => {
                if (t === defaultTagName.id) {
                  return `${defaultTagName.name}: ${totalResultsCount}`;
                }
                const p = tags.find((p) => p.id === t);
                return p ? `${p.name}: ${p.count}` : '';
              }}
            />
          )}
        </div>
        {!isLoading && (!totalResultsCount || totalResultsCount === 0) && (
          <EmptyPlaceholder
            title={searchData?.length > 0 ? 'За Вашим запитом нічого не знайдено' : 'Закороткий пошуковий запит'}
          />
        )}
        {!isLoading && totalResultsCount > 0 && (
          <div className={css.list}>
            {generateServiceItems()}
            {(searchTag === -1 || searchTag === 7) && courses.totalCount > 0 && (
              <CourseBlock search={searchData} courses={courses} />
            )}
          </div>
        )}
        {isLoading && <div className={css.skeletons}>{skeletons}</div>}
      </div>
    </main>
  );
}
